import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import './InstallerPanelTable.css';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { exportDataTableCsv } from '../../helpers/exportCsv';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Tooltip } from 'primereact/tooltip';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { FilterMatchMode } from 'primereact/api';
import { FilterService } from 'primereact/api';
import { DataView } from 'primereact/dataview';
import Snackbar from '../../../components/Snackbar/Snackbar';
import { ISystem } from '../../helpers/interfaces';
import { connect } from 'react-redux';
import { getSystemsVeryShort, getServiceActions, updateServiceAction, closeUpdateServiceActionSuccessNotification, closeUpdateServiceActionFailNotification,
         addServiceAction, addServiceActionAttachment, addServiceActionPartImage, closeCreateServiceActionSuccessNotification, closeCreateServiceActionFailNotification, deleteServiceAction } from '../../stores/actions/installerActions';
import ServiceActionFormDialog from './ServiceActionFormDialog';
import BatteryReplacementFormDialog from './BatteryReplacementFormDialog';
import { PerformersTags, performersLabel, performerIdsOf } from './ServiceActionPerformers';

interface ServiceActionsProps {
    systems: ISystem[];
    mobileView: boolean;
    dbServiceActions: any,
    updateServiceActionSuccess: boolean,
    updateServiceActionFail: boolean,
    createServiceActionSuccess: boolean,
    createServiceActionFail: boolean,
    getSystemsVeryShort: () => void;
    getServiceActions: () => void;
    updateServiceAction: (serviceAction) => void;
    addServiceAction: (serviceAction) => Promise<any>;
    deleteServiceAction: (serviceActionId) => void;
    addServiceActionAttachment: (attachment) => Promise<any>;
    addServiceActionPartImage: (image) => Promise<any>;
    closeUpdateServiceActionSuccessNotification: () => void;
    closeUpdateServiceActionFailNotification: () => void;
    closeCreateServiceActionSuccessNotification: () => void;
    closeCreateServiceActionFailNotification: () => void;
}

const ServiceActions = ({
    systems,
    mobileView,
    dbServiceActions,
    getServiceActions,
    getSystemsVeryShort,
    updateServiceAction,
    addServiceAction,
    deleteServiceAction,
    addServiceActionAttachment,
    addServiceActionPartImage,
    updateServiceActionSuccess,
    updateServiceActionFail,
    createServiceActionSuccess,
    createServiceActionFail,
    closeUpdateServiceActionSuccessNotification,
    closeUpdateServiceActionFailNotification,
    closeCreateServiceActionSuccessNotification,
    closeCreateServiceActionFailNotification
}: ServiceActionsProps) => {
    let emptyServiceAction = {
        ID: null,
        date: formatDateRemoveTime(new Date()),
        systemId: null,
        systemName: null,
        removedPartDesc: null,
        removedPartSerialNumber: null,
        installedPartDesc: null,
        installedPartSerialNumber: null,
        notes: null,
        underWarranty: false,
        removedPartImageData: null,
        removedPartImageFileType: null,
        installedPartImageData: null,
        installedPartImageFileType: null,
        removedPartName: null,
        installedPartName: null,
        batteryReplacement: false,
        performerIds: [],
        performers: [],
        pendingAttachments: [],
        pendingPartImages: []
    };

    const emptyBatteryReplacement = {
        ...emptyServiceAction,
        batteryReplacement: true,
        batteryReplacementPlanned: true,
    };

    useEffect(() => {
        getSystemsVeryShort();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (systems && dbServiceActions) {
            setServiceActions(dbServiceActions);
            const systemNamesByIds = Object.fromEntries(systems.map(x => [x.ID, x.name]));
            dbServiceActions?.forEach(sa =>{
                sa.systemName = systemNamesByIds[sa.systemId];
                sa.partSerialNumber = {
                    removedPartSerialNumber: sa.removedPartSerialNumber,
                    installedPartSerialNumber: sa.installedPartSerialNumber
                }
                // for filtering, sorting and export
                sa.performersLabel = performersLabel(sa.performers);
                sa.withInspection = !!sa.inspectionId;
            });
        }

    }, [dbServiceActions, systems]);

    useEffect(() => {
        if (updateServiceActionSuccess) {
            setTimeout(() => {
                closeUpdateServiceActionSuccessNotification();
            }, 6000);
            setServiceActionDialog(false);
            setBatteryReplacementDialog(false);
        }
    }, [updateServiceActionSuccess]);

    useEffect(() => {
        if (updateServiceActionFail) {
            setTimeout(() => {
                closeUpdateServiceActionFailNotification();
            }, 6000)
        }
    }, [updateServiceActionFail]);


    useEffect(() => {
        if (createServiceActionSuccess) {
            setTimeout(() => {
                closeCreateServiceActionSuccessNotification();
            }, 6000);
            setServiceActionDialog(false);
            setBatteryReplacementDialog(false);
        }
    }, [createServiceActionSuccess]);

    useEffect(() => {
        if (createServiceActionFail) {
            setTimeout(() => {
                closeCreateServiceActionFailNotification();
            }, 6000)
        }
    }, [createServiceActionFail]);

    useEffect(() => {
        if (systems) {
            setSystemNames(systems.map(el => el.name).sort());
            getServiceActions();
        }
    }, [systems]);

    const dt = useRef(null);

    const [systemNames, setSystemNames] = useState([]);
    const [serviceActionDialog, setServiceActionDialog] = useState(false);
    const [batteryReplacementDialog, setBatteryReplacementDialog] = useState(false);
    const [deleteServiceActionDialog, setDeleteServiceActionDialog] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [serviceActions, setServiceActions] = useState(dbServiceActions);
    const [serviceAction, setServiceAction] = useState(emptyServiceAction);


    // Mobile filter state
    const [mobileFilterDialog, setMobileFilterDialog] = useState(false);
    const [mobileFilters, setMobileFilters] = useState({
        systemName: '',
        serialNumber: '',       // searches both removed and installed SN
        removedPartDesc: '',
        removedPartName: '',
        installedPartDesc: '',
        installedPartName: '',
        notes: '',
        performedBy: '',
        underWarranty: null,
        batteryReplacement: null,
        withInspection: null,
        dateStart: null,
        dateEnd: null,
    });
    const [mobileSortField, setMobileSortField] = useState('ID');
    const [mobileSortOrder, setMobileSortOrder] = useState<-1 | 1>(-1);
    const [pendingFilters, setPendingFilters] = useState(mobileFilters);
    const [pendingSortField, setPendingSortField] = useState(mobileSortField);
    const [pendingSortOrder, setPendingSortOrder] = useState<-1 | 1>(mobileSortOrder);

    const openMobileFilterDialog = () => {
        setPendingFilters(mobileFilters);
        setPendingSortField(mobileSortField);
        setPendingSortOrder(mobileSortOrder);
        setMobileFilterDialog(true);
    };

    const applyMobileFilters = () => {
        setMobileFilters(pendingFilters);
        setMobileSortField(pendingSortField);
        setMobileSortOrder(pendingSortOrder);
        setMobileFilterDialog(false);
    };

    const resetMobileFilters = () => {
        const empty = {
            systemName: '', serialNumber: '', removedPartDesc: '',
            removedPartName: '', installedPartDesc: '', installedPartName: '',
            notes: '', performedBy: '', underWarranty: null, batteryReplacement: null, withInspection: null, dateStart: null, dateEnd: null
        };
        setPendingFilters(empty);
        setPendingSortField('ID');
        setPendingSortOrder(-1);
    };

    // Filtered + sorted list
    const filteredServiceActions = useMemo(() => {
        if (!serviceActions) return [];
        let result = [...serviceActions];

        if (mobileFilters.systemName)
            result = result.filter(s => s.systemName?.toLowerCase().includes(mobileFilters.systemName.toLowerCase()));
        if (mobileFilters.serialNumber) {
            const sn = mobileFilters.serialNumber.toLowerCase();
            result = result.filter(s =>
                s.removedPartSerialNumber?.toLowerCase().includes(sn) ||
                s.installedPartSerialNumber?.toLowerCase().includes(sn)
            );
        }
        if (mobileFilters.removedPartDesc)
            result = result.filter(s => s.removedPartDesc?.toLowerCase().includes(mobileFilters.removedPartDesc.toLowerCase()));
        if (mobileFilters.removedPartName)
            result = result.filter(s => s.removedPartName?.toLowerCase().includes(mobileFilters.removedPartName.toLowerCase()));
        if (mobileFilters.installedPartDesc)
            result = result.filter(s => s.installedPartDesc?.toLowerCase().includes(mobileFilters.installedPartDesc.toLowerCase()));
        if (mobileFilters.installedPartName)
            result = result.filter(s => s.installedPartName?.toLowerCase().includes(mobileFilters.installedPartName.toLowerCase()));
        if (mobileFilters.notes)
            result = result.filter(s => cleanNotes(s.notes)?.toLowerCase().includes(mobileFilters.notes.toLowerCase()));
        if (mobileFilters.performedBy)
            result = result.filter(s => s.performersLabel?.toLowerCase().includes(mobileFilters.performedBy.toLowerCase()));
        if (mobileFilters.underWarranty !== null)
            result = result.filter(s => s.underWarranty === mobileFilters.underWarranty);
        if (mobileFilters.withInspection !== null)
            result = result.filter(s => s.withInspection === mobileFilters.withInspection);
        if (mobileFilters.batteryReplacement !== null)
            result = result.filter(s => s.batteryReplacement === mobileFilters.batteryReplacement);
        if (mobileFilters.dateStart) {
            const start = new Date(mobileFilters.dateStart);
            start.setHours(0, 0, 0, 0);
            result = result.filter(s => s.date && new Date(s.date) >= start);
        }
        if (mobileFilters.dateEnd) {
            const end = new Date(mobileFilters.dateEnd);
            end.setHours(0, 0, 0, 0);
            result = result.filter(s => s.date && new Date(s.date) <= end);
        }

        result.sort((a, b) => {
            const aVal = a[mobileSortField] ?? '';
            const bVal = b[mobileSortField] ?? '';
            if (aVal < bVal) return -1 * mobileSortOrder;
            if (aVal > bVal) return 1 * mobileSortOrder;
            return 0;
        });

        return result;
    }, [serviceActions, mobileFilters, mobileSortField, mobileSortOrder]);

    // Badge count
    const activeFilterCount = useMemo(() => {
        return Object.entries(mobileFilters).filter(([key, v]) => {
            if (key === 'systemName') return false;
            return v !== '' && v !== null;
        }).length;
    }, [mobileFilters]);

    const sortOptions = [
        { label: 'ID', value: 'ID' },
        { label: 'System', value: 'systemName' },
        { label: 'Date', value: 'date' },
        { label: 'Inspection', value: 'withInspection' },
        { label: 'Performed by', value: 'performersLabel' },
        { label: 'Battery Replacement', value: 'batteryReplacement' },
        { label: 'Removed Part Desc', value: 'removedPartDesc' },
        { label: 'Removed Part SN', value: 'removedPartSerialNumber' },
        { label: 'Installed Part Desc', value: 'installedPartDesc' },
        { label: 'Installed Part SN', value: 'installedPartSerialNumber' },
        { label: 'Removed Part Name', value: 'removedPartName' },
        { label: 'Installed Part Name', value: 'installedPartName' },
        { label: 'Notes', value: 'notes' },
        { label: 'Under Warranty', value: 'underWarranty' },
    ];


    FilterService.register('custom_date_range', (value, filters) => {
        if (!value) return false;

        const rowDate = new Date(value);
        rowDate.setHours(0, 0, 0, 0);

        const start = filters?.dateStart ? new Date(filters.dateStart) : null;
        const end = filters?.dateEnd ? new Date(filters.dateEnd) : null;

        if (start) start.setHours(0, 0, 0, 0);
        if (end) end.setHours(0, 0, 0, 0);

        if (start && end) {
            return rowDate >= start && rowDate <= end;
        }

        if (start) {
            return rowDate >= start;
        }

        if (end) {
            return rowDate <= end;
        }

        return true;
    });

    FilterService.register('serial_number_both_columns', (value, filter) => {
        if (!filter || !filter.trim()) {
            return true;
        }

        const searchValue = filter.toLowerCase().trim();

        const removed = value?.removedPartSerialNumber?.toLowerCase() || '';
        const installed = value?.installedPartSerialNumber?.toLowerCase() || '';

        return (
            removed.includes(searchValue) ||
            installed.includes(searchValue)
        );
    });

    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        systemName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        removedPartDesc: { value: null, matchMode: FilterMatchMode.CONTAINS },
        removedPartName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        removedPartSerialNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
        installedPartDesc: { value: null, matchMode: FilterMatchMode.CONTAINS },
        installedPartName: { value: null, matchMode: FilterMatchMode.CONTAINS },
        installedPartSerialNumber: { value: null, matchMode: FilterMatchMode.CONTAINS },
        notes: { value: null, matchMode: FilterMatchMode.CONTAINS },
        underWarranty: { value: null, matchMode: FilterMatchMode.EQUALS },
        batteryReplacement: { value: null, matchMode: FilterMatchMode.EQUALS },
        withInspection: { value: null, matchMode: FilterMatchMode.EQUALS },
        performersLabel: { value: null, matchMode: FilterMatchMode.CONTAINS },
        date: { value: {dateStart: null, dateEnd: null}, matchMode: 'custom_date_range' },
        partSerialNumber: { value: null, matchMode: 'serial_number_both_columns' }
    });

    const validators = {
        description: (removedPartDesc: string | null, installedPartDesc: string | null) => !!removedPartDesc || !!installedPartDesc,
    };

    const warrantyOptions = [
        { label: 'YES', value: true },
        { label: 'NO', value: false }
    ];

    const inspectionOptions = [
        { label: 'With inspection', value: true },
        { label: 'Standalone', value: false }
    ];

    const openNew = () => {
        setServiceAction(emptyServiceAction);
        setServiceActionDialog(true);
    };

    const openNewBatteryReplacement = () => {
        setServiceAction(emptyBatteryReplacement);
        setBatteryReplacementDialog(true);
    };

    const hideDialog = () => {
        setServiceActionDialog(false);
    };

    const hideBatteryReplacementDialog = () => {
        setBatteryReplacementDialog(false);
    };

    const hideDeleteServiceActionDialog = () => {
        setDeleteServiceActionDialog(false);
    };

    const saveServiceAction = async () => {
        const partsValid = serviceAction.batteryReplacement || validators.description(serviceAction.removedPartDesc, serviceAction.installedPartDesc);
        if (!serviceAction.systemName || !partsValid) {
            return;
        }
        const systemIdsByNames = Object.fromEntries(systems.map(x => [x.name, x.ID]));
        const { pendingAttachments, pendingPartImages, ...rest } = serviceAction;
        const toSave = { ...rest, systemId: systemIdsByNames[serviceAction.systemName] };

        if (toSave.ID) {
            updateServiceAction(toSave);
            return;
        }

        const created = await addServiceAction(toSave);
        if (created && created.ID) {
            if (pendingAttachments && pendingAttachments.length > 0) {
                for (const att of pendingAttachments) {
                    await addServiceActionAttachment({
                        serviceActionId: created.ID,
                        fileData: att.fileData,
                        fileMimeType: att.fileMimeType,
                        fileSize: att.fileSize,
                        fileName: att.fileName,
                    });
                }
            }
            if (pendingPartImages && pendingPartImages.length > 0) {
                for (const img of pendingPartImages) {
                    await addServiceActionPartImage({
                        serviceActionId: created.ID,
                        partKind: img.partKind,
                        imageData: img.imageData,
                        imageFileType: img.imageFileType,
                    });
                }
            }
        }
    };

    const editServiceAction = (serviceAction) => {
        setServiceAction({ ...serviceAction, performerIds: performerIdsOf(serviceAction) });
        if (serviceAction.batteryReplacement) {
            setBatteryReplacementDialog(true);
        } else {
            setServiceActionDialog(true);
        }
    };

    const confirmDeleteServiceAction = (serviceAction) => {
        setServiceAction(serviceAction);
        setDeleteServiceActionDialog(true);
    };

    const deleteServiceActionItem = () => {
        deleteServiceAction(serviceAction.ID);
        let _serviceActions = serviceActions.filter((val) => val.ID !== serviceAction.ID);
        setServiceActions(_serviceActions);
        setDeleteServiceActionDialog(false);
        setServiceAction(emptyServiceAction);
    };

    function formatDateRemoveTime(date) {
        return date.getFullYear() +  "-" + String(date.getMonth() + 1).padStart(2, "0") + "-" + String(date.getDate()).padStart(2, "0");
    }

    const onServiceActionFormChange = (patch) => {
        setServiceAction({ ...serviceAction, ...patch });
    };

    const underWarrantyTemplate = (rowData) => {
        return <Tag value={rowData.underWarranty ? 'YES' : 'NO'} severity={rowData.underWarranty ? 'success' : 'info'}></Tag>
    };

    const batteryReplacementTemplate = (rowData) => {
        return <Tag value={rowData.batteryReplacement ? 'YES' : 'NO'} severity={rowData.batteryReplacement ? 'warning' : 'info'}></Tag>
    };

    // whether the service action was done with an inspection (added from it) or on its own
    const inspectionText = (rowData) => {
        if (!rowData.inspectionId) return 'Standalone';
        return rowData.inspectionProtocolNumber ? `Inspection ${rowData.inspectionProtocolNumber}` : 'With inspection';
    };

    const inspectionTemplate = (rowData) => {
        return <Tag value={inspectionText(rowData)} severity={rowData.inspectionId ? 'success' : 'secondary'}></Tag>
    };

    const performersTemplate = (rowData) => <PerformersTags performers={rowData.performers} />;

    const batteryReplacementPlannedTemplate = (rowData) => {
        return <Tag value={rowData.batteryReplacementPlanned ? 'Planned' : 'Malfunctioned'} severity={rowData.batteryReplacementPlanned ? 'info' : 'danger'}></Tag>
    };

    const dateTemplate = (rowData) => {
        if (rowData.date) {
            return (new Date(rowData.date)).toLocaleDateString('sv-SE');
        } 
        return null;
    };

    const notesTemplate = (rowData) => {
        if (rowData.notes) {
            const text = cleanNotes(rowData.notes);
            const tooltipId = `sa-notes-${rowData.ID}`;
            return ( mobileView ? text :
            <>
                <Tooltip target={`#${tooltipId}`} />
                <span 
                    id={tooltipId}
                    className="systemName-nodeName" 
                    data-pr-tooltip={text}
                    style={{
                        display: 'inline-block',
                        maxWidth: '100%',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                >
                    {text}
                </span>
            </>
        );
        } 
        return null;
    };

    const cleanNotes = (html) => {
        if (!html) return '';
        let text = html.replace(/<span class="ql-cursor">.*?<\/span>/g, '');
        text = text.replace(/<\/?[^>]+(>|$)/g, ' ');
        text = text.replace(/&nbsp;/g, ' ');
        text = text.replace(/&gt;/g, '>');
        text = text.replace(/&lt;/g, '<');
        text = text.replace(/\s+/g, ' ').trim();
        return text;
    };

    const underWarrantyFilterTemplate = (options) => {
        return (
            <Dropdown
                value={options.value}
                options={warrantyOptions}
                onChange={(e) => options.filterApplyCallback(e.value)}
                placeholder="Select"
                showClear
            />
        );
    };

    const inspectionFilterTemplate = (options) => {
        return (
            <Dropdown
                value={options.value}
                options={inspectionOptions}
                onChange={(e) => options.filterApplyCallback(e.value)}
                placeholder="Select"
                showClear
            />
        );
    };

    const batteryReplacementFilterTemplate = (options) => {
        return (
            <Dropdown
                value={options.value}
                options={warrantyOptions}
                onChange={(e) => options.filterApplyCallback(e.value)}
                placeholder="Select"
                showClear
            />
        );
    };

    const handleDateChange = (
        field: 'dateStart' | 'dateEnd',
        date: Date | null
    ) => {
        const updatedDate = date
            ? new Date(Date.UTC(
                date.getFullYear(),
                date.getMonth(),
                date.getDate()
            )).toISOString()
            : null;

        const newFilters = { ...filters };

        newFilters.date.value = {
            ...newFilters.date.value,
            [field]: updatedDate
        };

        setFilters(newFilters);
    };

    const dateFilter = (field: 'dateStart' | 'dateEnd', id?: string) => {
        const currentValue = filters.date.value?.[field];

        return (
            <Calendar
                id={id}
                value={currentValue ? new Date(currentValue) : null}
                onChange={(e) => handleDateChange(field, e.value)}
                placeholder="yyyy-mm-dd"
                dateFormat="yy-mm-dd"
                mask="9999-99-99"
                showButtonBar
            />
        );
    };

    const mobileFilterBar = (
        <div className="flex align-items-center gap-2 mb-3">
            <span className="p-input-icon-left flex-1">
                <InputText
                    value={mobileFilters.systemName}
                    onChange={(e) => setMobileFilters(prev => ({ ...prev, systemName: e.target.value }))}
                    placeholder="Search system..."
                    className="w-full"
                />
            </span>
            <div className="relative">
                <Button
                    icon="pi pi-filter"
                    rounded
                    outlined={activeFilterCount === 0}
                    severity={activeFilterCount > 0 ? 'info' : undefined}
                    onClick={openMobileFilterDialog}
                />
                {activeFilterCount > 0 && (
                    <span
                        className="absolute top-0 right-0 bg-primary text-white border-circle flex align-items-center justify-content-center"
                        style={{ width: '1.2rem', height: '1.2rem', fontSize: '0.65rem', transform: 'translate(30%, -30%)' }}
                    >
                        {activeFilterCount}
                    </span>
                )}
            </div>
            <Button icon="pi pi-plus" severity="warning" rounded onClick={openNewBatteryReplacement} tooltip="Battery replacement" />
            <Button icon="pi pi-plus" severity="success" rounded onClick={openNew} />
        </div>
    );    

    const cardTemplate = (rowData) => {
        return (
            <div className="col-12">
                <div className="border-1 surface-border border-round p-3 mb-2 surface-card">

                    <div className="flex justify-content-between align-items-center pb-2 mb-3 border-bottom-1 surface-border">
                        <span className="font-bold text-lg">{rowData.ID}</span>
                        {rowData.batteryReplacement && <Tag value="CU battery replacement" severity="warning"></Tag>}
                    </div>


                    <div className="mb-2 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">System</div>
                                <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.systemName || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Date</div>
                                <div className="font-medium">{dateTemplate(rowData)}</div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-2 p-2 surface-50 border-round">
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Performed by</div>
                                <div className="font-medium">{performersTemplate(rowData) ?? '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Inspection</div>
                                <div className="font-medium">{inspectionTemplate(rowData)}</div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-2 p-2 surface-50 border-round" style={{ display: rowData.batteryReplacement ? 'none' : undefined }}>
                        <div className="text-xs font-semibold text-color-secondary uppercase mb-2">
                            Removed Part
                        </div>
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Description</div>
                                <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.removedPartDesc || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Name in System</div>
                                <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.removedPartName || '---'}</div>
                            </div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Serial Number</div>
                                <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.removedPartSerialNumber || '---'}</div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-3 p-2 surface-50 border-round" style={{ display: rowData.batteryReplacement ? 'none' : undefined }}>
                        <div className="text-xs font-semibold text-color-secondary uppercase mb-2">
                            Installed Part
                        </div>
                        <div className="grid">
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Description</div>
                                <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.installedPartDesc || '---'}</div>
                            </div>
                            <div className="col-2"></div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Name in System</div>
                                <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.installedPartName || '---'}</div>
                            </div>
                            <div className="col-5">
                                <div className="text-xs text-color-secondary">Serial Number</div>
                                <div className="font-medium" style={{ wordBreak: 'break-word' }}>{rowData.installedPartSerialNumber || '---'}</div>
                            </div>
                        </div>
                    </div>

                    {!rowData.batteryReplacement && (
                        <div className="mb-3">
                            <div className="text-xs text-color-secondary mb-1">Notes</div>
                            <div className="font-medium">{notesTemplate(rowData)}</div>
                        </div>
                    )}

                    <div className="flex justify-content-between align-items-center pt-2 border-top-1 surface-border">
                        <div className="flex flex-column">
                            {rowData.batteryReplacement ? (
                                <React.Fragment>
                                    <span className="text-sm text-color-secondary">Reason</span>
                                    <span className="font-medium">{batteryReplacementPlannedTemplate(rowData)}</span>
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    <span className="text-sm text-color-secondary">Under Warranty</span>
                                    <span className="font-medium">{underWarrantyTemplate(rowData)}</span>
                                </React.Fragment>
                            )}
                        </div>
                        <div>
                            {actionBodyTemplate(rowData)}
                        </div>
                    </div>

                </div>
            </div>
        );
    };
    
    const mobileFilterDialogFooter = (
        <div className="flex justify-content-between">
            <Button label="Reset" icon="pi pi-times" text severity="secondary" onClick={resetMobileFilters} />
            <div className="flex gap-2">
                <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setMobileFilterDialog(false)} />
                <Button label="Apply" icon="pi pi-check" onClick={applyMobileFilters} />
            </div>
        </div>
    );

    const actionBodyTemplate = (rowData) => {
        return (
            <React.Fragment>
                <Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => editServiceAction(rowData)} />
                <Button icon="pi pi-trash" rounded outlined severity="danger" onClick={() => confirmDeleteServiceAction(rowData)} />
            </React.Fragment>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <div className="flex flex-wrap gap-2">
                <Button label="Export" icon="pi pi-file-export" outlined onClick={() => exportDataTableCsv(dt, 'service-actions')} />
            </div>
        );
    };

    const leftToolbarTemplate = () => {
        return (
            <div className="flex flex-wrap gap-2">
                <Button label="Battery replacement" icon="pi pi-plus" severity="warning" onClick={openNewBatteryReplacement} />
                <Button label="New" icon="pi pi-plus" severity="success" onClick={openNew} />
            </div>
        );
    };
    const deleteServiceActionDialogFooter = (
        <React.Fragment>
            <Button label="No" icon="pi pi-times" outlined onClick={hideDeleteServiceActionDialog} />
            <Button label="Yes" icon="pi pi-check" severity="danger" onClick={deleteServiceActionItem} />
        </React.Fragment>
    );

    const renderHeader = () => {
        return (
            <div className="flex justify-content-end">
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText 
                        value={filters.partSerialNumber?.value || ''} 
                        onChange={(e) => {
                            const newFilters = { ...filters };
                            newFilters.partSerialNumber.value = e.target.value;
                            setFilters(newFilters);
                        }}
                        placeholder="Search in both SN columns" />
                </IconField>
            </div>
        );
    };

    const header = renderHeader();

    const dataView = useMemo(() => {
        return (
            <div>
                {mobileFilterBar}
                <DataView
                    value={filteredServiceActions}
                    itemTemplate={cardTemplate}
                    paginator
                    rows={10}
                />
            </div>
        );
    }, [filteredServiceActions, mobileFilters, mobileFilterDialog, createServiceActionSuccess, updateServiceActionSuccess]);

    const dataTable = useMemo(() => {
        return (
            <div className="card">
                <Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

                <DataTable ref={dt} value={serviceActions} sortField="ID" sortOrder={-1} header={header}
                        dataKey="ID"  paginator rows={25} rowsPerPageOptions={[10, 25, 50, 100]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} items" globalFilter={globalFilter} 
                        filters={filters} filterDisplay="row" stripedRows size='small' resizableColumns
                        exportFilename="service-actions"
                        exportFunction={(e) => {
                            if (e.field === 'notes') return cleanNotes(e.data);
                            if (e.field === 'date') return dateTemplate(e.rowData) ?? '';
                            if (e.field === 'withInspection') return inspectionText(e.rowData);
                            return e.data;
                        }}>
                <Column exportable={false}></Column>
                <Column                    
                    field="ID" 
                    header="ID"
                    style={{maxWidth: '45px'}}
                    sortable>
                </Column>
                <Column 
                    field="systemName" 
                    header="System"
                    sortable
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column
                    field="date"
                    header="Date"
                    body={dateTemplate}
                    sortable
                    filter
                    showClearButton={false}
                    showFilterMenu={false}
                    filterElement={() => (
                        <div className="flex flex-col gap-1">
                            {dateFilter('dateStart')}-{dateFilter('dateEnd')}
                        </div>
                    )}>
                </Column>
                <Column
                    field="withInspection"
                    header="Inspection"
                    body={inspectionTemplate}
                    sortable
                    style={{maxWidth: '150px'}}
                    filterPlaceholder=""
                    showFilterMenu={false}
                    filter
                    filterField="withInspection"
                    filterElement={inspectionFilterTemplate}>
                </Column>
                <Column
                    field="performersLabel"
                    header="Performed by"
                    body={performersTemplate}
                    sortable
                    style={{maxWidth: '200px'}}
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column
                    field="batteryReplacement"
                    header="Battery Replacement"
                    body={batteryReplacementTemplate}
                    sortable
                    style={{maxWidth: '110px'}}
                    filterPlaceholder=""
                    showFilterMenu={false}
                    filter
                    filterField="batteryReplacement"
                    filterElement={batteryReplacementFilterTemplate}>
                </Column>
                <Column
                    field="removedPartDesc"
                    header="Removed Part Desc"
                    sortable
                    style={{maxWidth: '250px'}}
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="removedPartSerialNumber" 
                    header="Removed Part SN" 
                    sortable
                    style={{maxWidth: '200px'}}
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column
                    field="removedPartName"
                    header="Removed Part Name in System"
                    sortable
                    style={{maxWidth: '200px'}}
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="installedPartDesc" 
                    header="Installed Part Desc" 
                    sortable
                    style={{maxWidth: '250px'}}
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="installedPartSerialNumber" 
                    header="Installed Part SN" 
                    sortable
                    style={{maxWidth: '200px'}}
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column
                    field="installedPartName"
                    header="Installed Part Name in System"
                    sortable
                    style={{maxWidth: '200px'}}
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="notes" 
                    header="Notes"
                    style={{maxWidth: '200px'}}
                    body={notesTemplate}
                    sortable
                    filter filterPlaceholder=""
                    showFilterMenu={false}>
                </Column>
                <Column 
                    field="underWarranty" 
                    header="Under Warranty"
                    body={underWarrantyTemplate}
                    sortable
                    filterPlaceholder=""
                    showFilterMenu={false}
                    filter
                    filterField="underWarranty"
                    filterElement={underWarrantyFilterTemplate}
                    >
                </Column>
                <Column body={actionBodyTemplate} exportable={false} style={{ minWidth: '12rem', maxWidth: '24rem' }}></Column>
                </DataTable>
            </div>
        );
    }, [serviceActions, filters]);


    return (
        <div>
            { mobileView ? dataView : dataTable }

            <ServiceActionFormDialog
                visible={serviceActionDialog}
                mobileView={mobileView}
                title={serviceAction.ID ? 'Edit item #' + serviceAction.ID : 'Add new item'}
                value={serviceAction}
                onChange={onServiceActionFormChange}
                onHide={hideDialog}
                onSave={saveServiceAction}
                systemNames={systemNames}
            />

            <BatteryReplacementFormDialog
                visible={batteryReplacementDialog}
                mobileView={mobileView}
                title={serviceAction.ID ? 'Edit battery replacement #' + serviceAction.ID : 'Add battery replacement'}
                value={serviceAction}
                onChange={onServiceActionFormChange}
                onHide={hideBatteryReplacementDialog}
                onSave={saveServiceAction}
                systemNames={systemNames}
            />

            <Dialog visible={deleteServiceActionDialog} style={{ width: '32rem' }} breakpoints={{ '960px': '75vw', '641px': '90vw' }} header="Confirm" modal footer={deleteServiceActionDialogFooter} onHide={hideDeleteServiceActionDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    {serviceAction && (
                        <span>
                            Are you sure you want to delete this item (#{serviceAction.ID})?
                        </span>
                    )}
                </div>
            </Dialog>

            <Dialog
                visible={mobileFilterDialog}
                onHide={() => setMobileFilterDialog(false)}
                header="Filter & Sort"
                style={{ width: '95vw' }}
                footer={mobileFilterDialogFooter}
                modal
                className="p-fluid"
            >
                <div className="mb-4">
                    <label className="font-bold mb-2 block">Sort by</label>
                    <div className="flex gap-2">
                        <Dropdown
                            value={pendingSortField}
                            options={sortOptions}
                            onChange={(e) => setPendingSortField(e.value)}
                            className="flex-1"
                        />
                        <Button
                            icon={pendingSortOrder === 1 ? 'pi pi-sort-amount-up' : 'pi pi-sort-amount-down'}
                            outlined
                            onClick={() => setPendingSortOrder(prev => prev === 1 ? -1 : 1)}
                            tooltip={pendingSortOrder === 1 ? 'Ascending' : 'Descending'}
                        />
                    </div>
                </div>

                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Date from</label>
                    <Calendar
                        value={pendingFilters.dateStart ? new Date(pendingFilters.dateStart) : null}
                        onChange={(e) => {
                            const date = e.value as Date | null;
                            if (!date) { setPendingFilters(prev => ({ ...prev, dateStart: null })); return; }
                            const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                            setPendingFilters(prev => ({ ...prev, dateStart: utc.toISOString() }));
                        }}
                        placeholder="yyyy-mm-dd" dateFormat="yy-mm-dd" mask="9999-99-99"
                        showButtonBar className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Date to</label>
                    <Calendar
                        value={pendingFilters.dateEnd ? new Date(pendingFilters.dateEnd) : null}
                        onChange={(e) => {
                            const date = e.value as Date | null;
                            if (!date) { setPendingFilters(prev => ({ ...prev, dateEnd: null })); return; }
                            const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                            setPendingFilters(prev => ({ ...prev, dateEnd: utc.toISOString() }));
                        }}
                        placeholder="yyyy-mm-dd" dateFormat="yy-mm-dd" mask="9999-99-99"
                        showButtonBar className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Serial Number (both columns)</label>
                    <InputText
                        value={pendingFilters.serialNumber}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, serialNumber: e.target.value }))}
                        placeholder="Search removed or installed SN..."
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Removed Part Desc</label>
                    <InputText
                        value={pendingFilters.removedPartDesc}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, removedPartDesc: e.target.value }))}
                        placeholder="Search..."
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Installed Part Desc</label>
                    <InputText
                        value={pendingFilters.installedPartDesc}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, installedPartDesc: e.target.value }))}
                        placeholder="Search..."
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Notes</label>
                    <InputText
                        value={pendingFilters.notes}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Search..."
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Under Warranty</label>
                    <Dropdown
                        value={pendingFilters.underWarranty}
                        options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, underWarranty: e.value }))}
                        placeholder="Any" showClear className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Battery Replacement</label>
                    <Dropdown
                        value={pendingFilters.batteryReplacement}
                        options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, batteryReplacement: e.value }))}
                        placeholder="Any" showClear className="w-full"
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Performed by</label>
                    <InputText
                        value={pendingFilters.performedBy}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, performedBy: e.target.value }))}
                        placeholder="Search..."
                    />
                </div>
                <div className="mb-3">
                    <label className="text-sm text-color-secondary mb-1 block">Inspection</label>
                    <Dropdown
                        value={pendingFilters.withInspection}
                        options={inspectionOptions}
                        onChange={(e) => setPendingFilters(prev => ({ ...prev, withInspection: e.value }))}
                        placeholder="Any" showClear className="w-full"
                    />
                </div>
            </Dialog>


            <Snackbar
                close
                closeNotification={() => closeUpdateServiceActionSuccessNotification()}
                color='success'
                message={'Service action updated'}
                open={updateServiceActionSuccess}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeUpdateServiceActionFailNotification()}
                color='danger'
                message={'Update error'}
                open={updateServiceActionFail}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeCreateServiceActionSuccessNotification()}
                color='success'
                message={'Service action created'}
                open={createServiceActionSuccess}
                place='tc'
            />
            <Snackbar
                close
                closeNotification={() => closeCreateServiceActionFailNotification()}
                color='danger'
                message={'Create error'}
                open={createServiceActionFail}
                place='tc'
            />
        </div>
    );
}


const mapStateToProps = (state) => {
    return {
        systems: state.dashboard.systemsVeryShort,
        mobileView: state.dashboard.mobileView,
        dbServiceActions: state.dashboard.serviceActions,
        updateServiceActionSuccess: state.dashboard.updateServiceActionSuccess,
        updateServiceActionFail: state.dashboard.updateServiceActionFail,
        createServiceActionSuccess: state.dashboard.createServiceActionSuccess,
        createServiceActionFail: state.dashboard.createServiceActionFail
    };
};

const actions = {
    getSystemsVeryShort,
    getServiceActions,
    updateServiceAction,
    addServiceAction,
    deleteServiceAction,
    addServiceActionAttachment,
    addServiceActionPartImage,
    closeUpdateServiceActionSuccessNotification,
    closeUpdateServiceActionFailNotification,
    closeCreateServiceActionSuccessNotification,
    closeCreateServiceActionFailNotification
};

export default connect(mapStateToProps, actions)(ServiceActions);
